import { useEditorData, useEditorHistory, useTooling } from '../lib/FabricJs';
import { CANVAS_JSON_PROPS } from '../config';
import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  CornerUpLeft,
  CornerUpRight,
  Monitor,
  RefreshCw,
} from 'react-feather';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import styled from 'styled-components';
import { useLockSlideById, useUpdateSlide } from '../../../api-http/slides-v2';
import { UpdateSlideTitle } from '../../../types/slide.types';
import {
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { DEBOUNCE_TIME } from '../../../configs';
import { Socket } from 'socket.io-client';
import { useSnack } from '../../../util/useSnack';
import { getControlButtonText } from '../utils/utils';
import { editorTheme, HEADER_HEIGHT } from '../theme';
import {
  SaveStatus,
  useSlideSave,
} from '../lib/FabricJs/providers/SaveProvider';

const HeaderContainer = styled.header`
  height: ${HEADER_HEIGHT}px;
  flex: none;
  background: ${editorTheme.surface};
  border-bottom: 1px solid ${editorTheme.border};
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px 0 8px;
`;

const VerticalLine = styled.div`
  background: ${editorTheme.border};
  height: 20px;
  width: 1px;
`;

const HomeBack = styled(Link)`
  color: ${editorTheme.text};
  font-size: 14px;
  font-weight: 500;
  padding: 6px 8px;
  border-radius: 6px;
  text-decoration: none;
  align-items: center;
  display: flex;
  gap: 2px;
  &:hover {
    background: ${editorTheme.surfaceMuted};
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TitleCaption = styled.span`
  font-size: 11px;
  color: ${editorTheme.textMuted};
  padding: 0 4px;
`;

const TitleInput = styled.input`
  color: ${editorTheme.text};
  font-size: 15px;
  font-weight: 600;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0 4px;
  width: 280px;
  max-width: 100%;
  &:hover {
    border-color: ${editorTheme.border};
  }
  &:focus {
    outline: none;
    border-color: ${editorTheme.accent};
  }
`;

const STATUS_STYLES: Record<
  SaveStatus,
  { color: string; background: string; label: string }
> = {
  saved: {
    color: editorTheme.success,
    background: editorTheme.successSoft,
    label: 'Saved',
  },
  unsaved: {
    color: editorTheme.warning,
    background: editorTheme.warningSoft,
    label: 'Unsaved changes',
  },
  saving: {
    color: editorTheme.textMuted,
    background: editorTheme.surfaceMuted,
    label: 'Saving…',
  },
  error: {
    color: editorTheme.danger,
    background: editorTheme.dangerSoft,
    label: 'Not saved',
  },
};

const StatusPill = styled.span<{ status: SaveStatus }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  color: ${({ status }) => STATUS_STYLES[status].color};
  background: ${({ status }) => STATUS_STYLES[status].background};
`;

const Spacer = styled.div`
  flex: 1;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${editorTheme.text};
  background: none;
  &:hover:not(:disabled) {
    background: ${editorTheme.surfaceMuted};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const TextButton = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  background: none;
  color: ${editorTheme.textMuted};
  font-size: 13px;
  font-weight: 500;
  &:hover {
    background: ${editorTheme.surfaceMuted};
    color: ${editorTheme.text};
  }
`;

const PreviewButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${editorTheme.border};
  background: ${editorTheme.surface};
  color: ${editorTheme.text};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    border-color: ${editorTheme.accent};
    color: ${editorTheme.accentHover};
  }
`;

const SaveButton = styled.button`
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  background: ${editorTheme.accent};
  color: white;
  font-size: 13px;
  font-weight: 600;
  &:hover:not(:disabled) {
    background: ${editorTheme.accentHover};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const statusIcon = (status: SaveStatus) => {
  switch (status) {
    case 'saved':
      return <Check size={14} />;
    case 'saving':
      return <RefreshCw size={13} />;
    case 'unsaved':
    case 'error':
      return <AlertCircle size={13} />;
  }
};

interface HeaderProps {
  onClickPreview: (json: object) => void;
  updateTitle: UpdateSlideTitle | undefined;
  socket: Socket;
}

const Header: FC<HeaderProps> = ({ onClickPreview, updateTitle, socket }) => {
  const {
    slidesProps: { paramId, slide },
  } = useTooling();
  const { canvas } = useEditorData();
  const { undo, redo, canUndo, canRedo } = useEditorHistory();
  const { status, save, discard, leaveWithoutSaving } = useSlideSave();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'leave' | 'discard' | null>(null);

  const { mutate } = useUpdateSlide();
  const { state } = useLocation();
  const { mutate: mutateSlideStatus } = useLockSlideById();
  const { showErrSnack } = useSnack();

  const slidesUrl = state?.search ? `/slides${state?.search}` : '/slides';
  const backUrl = searchParams.get('goBackUrl') ?? slidesUrl;
  const controlButtonText = getControlButtonText();
  const deviceInfo = slide?.projectData?.deviceInfo;
  const hasUnsavedChanges = status !== 'saved';

  const onBrowserBack = useCallback(() => {
    mutateSlideStatus(
      { isEditing: false, id: paramId ?? '' },
      {
        onSuccess: () => {
          socket.disconnect();
        },
        onError: (err) => {
          showErrSnack(err.msg);
        },
      },
    );
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', onBrowserBack);

    return () => {
      window.removeEventListener('popstate', onBrowserBack);
    };
  }, []);

  const onClickBack = (socket: Socket, paramId: string) => {
    if (paramId) {
      mutateSlideStatus(
        { isEditing: false, id: paramId },
        {
          onSuccess: () => {
            socket.disconnect();
          },
          onError: (err) => {
            showErrSnack(err.msg);
          },
        },
      );
    }
  };

  const leave = () => {
    setDialog(null);
    onClickBack(socket, paramId || '');
    navigate(backUrl);
  };

  const onBackLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      setDialog('leave');
      return;
    }
    onClickBack(socket, paramId || '');
  };

  return (
    <HeaderContainer>
      <HomeBack
        to={backUrl}
        aria-label="Back to slides"
        onClick={onBackLinkClick}
      >
        <ChevronLeft size={18} /> Slides
      </HomeBack>
      <VerticalLine />
      <TitleBlock>
        {deviceInfo?.width && deviceInfo?.height && (
          <TitleCaption>
            {deviceInfo.width} × {deviceInfo.height}
          </TitleCaption>
        )}
        <TitleInput
          defaultValue={updateTitle?.title || ''}
          type="text"
          placeholder="Design Title"
          aria-label="Design title"
          onChange={debounce((e) => {
            mutate({
              id: updateTitle?._id || '',
              title: e.target.value,
            });
          }, DEBOUNCE_TIME)}
        />
      </TitleBlock>
      <StatusPill status={status} role="status">
        {statusIcon(status)} {STATUS_STYLES[status].label}
      </StatusPill>
      <Spacer />
      <Tooltip title={`Undo (${controlButtonText} + Z)`}>
        <span>
          <IconButton aria-label="Undo" onClick={undo} disabled={!canUndo}>
            <CornerUpLeft size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Redo (${controlButtonText} + Shift + Z)`}>
        <span>
          <IconButton aria-label="Redo" onClick={redo} disabled={!canRedo}>
            <CornerUpRight size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <VerticalLine />
      {(status === 'unsaved' || status === 'error') && (
        <TextButton onClick={() => setDialog('discard')}>Discard</TextButton>
      )}
      <PreviewButton
        aria-label="Preview Design"
        onClick={() =>
          canvas && onClickPreview(canvas.toJSON(CANVAS_JSON_PROPS))
        }
      >
        <Monitor size={16} />
        Preview
      </PreviewButton>
      <Tooltip title={`Save (${controlButtonText} + S)`}>
        <span>
          <SaveButton
            onClick={() => save()}
            disabled={status === 'saved' || status === 'saving'}
          >
            Save
          </SaveButton>
        </span>
      </Tooltip>

      <Dialog
        open={dialog === 'leave'}
        onClose={() => setDialog(null)}
        aria-labelledby="leave-dialog-title"
      >
        <DialogTitle id="leave-dialog-title">Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This slide has changes that are not saved. Screens keep showing the
            last saved version.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              leaveWithoutSaving();
              leave();
            }}
          >
            Leave without saving
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (await save()) leave();
              else setDialog(null);
            }}
          >
            Save and leave
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialog === 'discard'}
        onClose={() => setDialog(null)}
        aria-labelledby="discard-dialog-title"
      >
        <DialogTitle id="discard-dialog-title">
          Discard unsaved changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The slide goes back to how it was last saved. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={discard}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </HeaderContainer>
  );
};

export default Header;
